import { useCallback, useEffect, useState } from "react"

import {
  type DesktopNotificationPermission,
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission
} from "../../../utils/notification/browser-notification.ts"
import { subscribeToWebPush } from "../../../utils/notification/web-push.ts"
import { anyDesktopOn } from "../helpers/desktopPreference.ts"
import type { NotificationSettings } from "../types/notification.types"

/** This browser's answer for desktop alerts, and the asks that change it. */
export function useDesktopPermission(settings: NotificationSettings | null) {
  const [permission, setPermission] = useState<DesktopNotificationPermission>(
    getDesktopNotificationPermission
  )

  // A Desktop choice delivers nothing to this browser until it holds a push
  // subscription, and permission alone never creates one. Asking needs a click,
  // so a choice that is on by default waits for "Allow".
  const ask = useCallback(async () => {
    const answer = await requestDesktopNotificationPermission()
    setPermission(answer)
    if (answer === "granted") void subscribeToWebPush()
  }, [])

  // Turning the Desktop channel on is the explicit action the browser requires
  // before it will prompt — asking on page load would be refused, and asking
  // repeatedly is what gets a site permanently blocked. Once the answer is
  // "denied" or "unsupported" the browser will not ask again, so we stop
  // asking and explain instead.
  const onDesktopTurnedOn = useCallback(() => {
    const current = getDesktopNotificationPermission()
    if (current !== "default") {
      setPermission(current)
      if (current === "granted") void subscribeToWebPush()
      return
    }
    void ask()
  }, [ask])

  const wanted = anyDesktopOn(settings)

  useEffect(() => {
    if (wanted && getDesktopNotificationPermission() === "granted") {
      void subscribeToWebPush()
    }
  }, [wanted])

  const blocked = permission === "denied" || permission === "unsupported"

  return {
    permission,
    wanted,
    blocked,
    notYetAllowed: wanted && permission === "default",
    ask,
    onDesktopTurnedOn
  }
}

export type DesktopPermissionState = ReturnType<typeof useDesktopPermission>
